import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, InputAdornment, ListItemText, MenuItem, Snackbar, Stack, Switch, Tab, Tabs, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Add, DeleteOutline, NetworkCheck, Visibility, VisibilityOff } from '@mui/icons-material';
import FormDrawer from '../../components/ui/FormDrawer';
import api from '../../services/api';
import { hasPermissionMatch } from '../../utils/permissions';

type Env = 'sandbox' | 'live';
type Verification = { status: 'verified' | 'failed' | 'unverified'; message: string; at: string | null };
type EnvCredentials = {
  complete: boolean;
  issues: string[];
  fields: Array<{ key: string; label: string; secret: boolean; required: boolean; set: boolean; preview: string; formatHint: string | null }>;
  verification: Verification;
};
type GatewayRow = {
  type: string;
  label: string;
  kind: 'online' | 'offline';
  checkout: boolean;
  testable: boolean;
  setUp: boolean;
  enabled: boolean;
  priority: number | null;
  environment: Env | null;
  credentials: Record<Env, EnvCredentials> | null;
  readiness: { state: string; label: string; detail: string };
  ready: boolean;
  routing: { role: 'default' | 'backup' | 'fallback' | null; order: number | null };
  activeMethods: Array<{ code: string; name: string }>;
  transactions: number;
  toggleLock: string | null;
  deleteLock: string | null;
};
type CatalogField = {
  key: string; label: string; secret: boolean; required: boolean; minLength: number;
  pattern: Record<Env, string | null>; formatHint: Record<Env, string | null>;
};
type CatalogEntry = { type: string; label: string; kind: 'online' | 'offline'; checkout: boolean; testable: boolean; added: boolean; fields: CatalogField[] };
type AdminView = {
  gateways: GatewayRow[];
  routing: { defaultType: string | null; order: string[]; message: string };
  catalog: CatalogEntry[];
  priorityRange: { min: number; max: number };
  takenPriorities: Array<{ type: string; priority: number }>;
};

const ENV_LABEL: Record<Env, string> = { sandbox: 'Sandbox', live: 'Live' };

const READINESS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  verified: 'success',
  unverified: 'warning',
  failed: 'error',
  incomplete: 'error',
  not_integrated: 'default',
  no_credentials: 'info',
  not_set_up: 'default',
};

const apiMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || (err as Error)?.message || fallback;

type FormState = {
  type: string;
  environment: Env | '';
  priority: string;
  enabled: boolean;
  credentials: Record<Env, Record<string, string>>;
};

const emptyForm = (): FormState => ({ type: '', environment: '', priority: '', enabled: false, credentials: { sandbox: {}, live: {} } });

function fieldError(field: CatalogField, env: Env, value: string, saved: boolean, required: boolean): string | undefined {
  if (!value) return required && field.required && !saved ? `${field.label} is required for ${ENV_LABEL[env]}` : undefined;
  if (field.minLength && value.length < field.minLength) return `${field.label} looks too short`;
  const pattern = field.pattern[env];
  if (pattern && !new RegExp(pattern).test(value)) return `Must be a ${ENV_LABEL[env]} key (${field.formatHint[env] || 'invalid format'})`;
  return undefined;
}

function GatewayFormDrawer({
  open, mode, row, view, onClose, onSaved,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  row: GatewayRow | null;
  view: AdminView | undefined;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [tab, setTab] = useState<Env>('sandbox');
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; environment: Env } | null>(null);

  useEffect(() => {
    if (!open) return;
    setReveal({});
    setTestResult(null);
    if (row) {
      const env = row.environment || '';
      setForm({ ...emptyForm(), type: row.type, environment: env, priority: row.setUp && row.priority != null ? String(row.priority) : '', enabled: row.enabled });
      setTab(env || 'sandbox');
    } else {
      setForm(emptyForm());
      setTab('sandbox');
    }
  }, [open, row]);

  const def = view?.catalog.find((c) => c.type === form.type);
  const isOnline = def?.kind === 'online';
  const env = form.environment;
  const savedFields = (e: Env) => row?.credentials?.[e]?.fields || [];
  const isSaved = (e: Env, key: string) => savedFields(e).some((x) => x.key === key && x.set);

  const errors = useMemo(() => {
    const out: string[] = [];
    const fieldErrors: Record<string, string> = {};
    if (mode === 'create' && !form.type) out.push('Select a gateway type');
    if (isOnline && !env) out.push('Select the environment (Sandbox or Live)');
    const p = Number(form.priority);
    const range = view?.priorityRange || { min: 1, max: 99 };
    if (!form.priority.trim()) out.push('Enter a priority');
    else if (!Number.isInteger(p) || p < range.min || p > range.max) out.push(`Priority must be a whole number from ${range.min} to ${range.max}`);
    else if (form.enabled) {
      const clash = view?.takenPriorities.find((t) => t.type !== form.type && t.priority === p);
      if (clash) out.push(`Priority ${p} is already used by ${view?.catalog.find((c) => c.type === clash.type)?.label || clash.type}`);
    }
    if (def && isOnline) {
      for (const e of ['sandbox', 'live'] as Env[]) {
        for (const field of def.fields) {
          const msg = fieldError(field, e, (form.credentials[e][field.key] || '').trim(), isSaved(e, field.key), e === env);
          if (msg) { fieldErrors[`${e}.${field.key}`] = msg; out.push(`${ENV_LABEL[e]}: ${msg}`); }
        }
      }
    }
    if (form.enabled && def && !def.checkout) out.push(`${def.label} is not integrated with checkout, so it cannot be enabled`);
    return { list: Array.from(new Set(out)), fields: fieldErrors };
  }, [form, def, isOnline, env, view, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentEnvComplete = !!def && (!isOnline || (!!env && def.fields.every((f) => !f.required || (form.credentials[env as Env][f.key] || '').trim() || isSaved(env as Env, f.key))
    && !Object.keys(errors.fields).some((k) => k.startsWith(`${env}.`))));
  const canEnable = !!def?.checkout && currentEnvComplete;

  useEffect(() => {
    if (form.enabled && !canEnable) setForm((prev) => ({ ...prev, enabled: false }));
  }, [canEnable, form.enabled]);

  const credentialPayload = () => {
    if (!isOnline) return undefined;
    const out: Partial<Record<Env, Record<string, string>>> = {};
    for (const e of ['sandbox', 'live'] as Env[]) {
      const entries = Object.entries(form.credentials[e]).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v);
      if (entries.length) out[e] = Object.fromEntries(entries);
    }
    return out;
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        type: form.type,
        environment: isOnline ? form.environment : undefined,
        priority: Number(form.priority),
        enabled: form.enabled,
        credentials: credentialPayload(),
      };
      if (mode === 'create') return api.post('/payments/gateways', payload);
      return api.put(`/payments/gateways/${form.type}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-gateways-admin'] });
      queryClient.invalidateQueries({ queryKey: ['master-data'] });
      onSaved(`${def?.label || 'Gateway'} ${mode === 'create' ? 'added' : 'saved'}${form.enabled ? ' and enabled' : ''}`);
      onClose();
    },
  });

  const testMut = useMutation({
    mutationFn: async (testEnv: Env) => {
      const res = await api.post(`/payments/gateways/${form.type}/test`, {
        environment: testEnv,
        credentials: Object.fromEntries(Object.entries(form.credentials[testEnv]).filter(([, v]) => v.trim())),
      });
      return res.data.data as { ok: boolean; message: string; environment: Env };
    },
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: ['payment-gateways-admin'] });
    },
    onError: (err) => setTestResult({ ok: false, message: apiMessage(err, 'Test failed'), environment: tab }),
  });

  useEffect(() => { if (open) saveMut.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const takenText = (view?.takenPriorities || [])
    .filter((t) => t.type !== form.type)
    .sort((a, b) => a.priority - b.priority)
    .map((t) => `${view?.catalog.find((c) => c.type === t.type)?.label || t.type} ${t.priority}`)
    .join(', ');

  const availableTypes = (view?.catalog || []).filter((c) => !c.added);
  const tabFields = def?.fields || [];
  const tabSaved = row?.credentials?.[tab];

  return (
    <FormDrawer
      open={open}
      title={mode === 'create' ? 'Add payment gateway' : `Configure ${row?.label || ''}`}
      onClose={onClose}
      onSubmit={() => { if (!errors.list.length) saveMut.mutate(); }}
      submitLabel={mode === 'create' ? 'Save gateway' : 'Save'}
      submitHint={errors.list.length ? `Save is blocked — ${errors.list.length} issue${errors.list.length === 1 ? '' : 's'} listed above` : undefined}
      disableSubmit={errors.list.length > 0 || !view}
      loading={saveMut.isPending}
      width={620}
    >
      <Stack spacing={2.25}>
        {saveMut.isError && <Alert severity="error">{apiMessage(saveMut.error, 'Could not save gateway')}</Alert>}

        {mode === 'create' ? (
          <TextField
            select
            required
            fullWidth
            label="Gateway type"
            value={form.type}
            onChange={(e) => {
              const next = view?.catalog.find((c) => c.type === e.target.value);
              setForm({ ...emptyForm(), type: e.target.value, priority: next?.type === 'cash' ? '99' : '' });
              setTestResult(null);
            }}
            helperText={def
              ? (def.checkout ? `${def.kind === 'online' ? 'Online' : 'Offline'} gateway supported by checkout` : `${def.label} can be saved for later, but checkout cannot process it yet, so it cannot be enabled`)
              : 'Only supported gateways that are not added yet are listed'}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="" disabled><em>Select gateway</em></MenuItem>
            {availableTypes.map((c) => (
              <MenuItem key={c.type} value={c.type}>
                <ListItemText
                  primary={c.label}
                  secondary={`${c.kind === 'online' ? 'Online' : 'Offline'} · ${c.checkout ? 'Supported by checkout' : 'Not integrated with checkout yet'}`}
                />
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField fullWidth label="Gateway type" value={row?.label || ''} disabled helperText="Gateway type cannot be changed — remove it and add another instead" />
        )}

        {isOnline && (
          <Box>
            <Typography variant="body2" fontWeight={650} sx={{ mb: 0.75 }}>Environment *</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.environment || null}
              onChange={(_, v: Env | null) => { if (v) { setForm({ ...form, environment: v }); setTab(v); setTestResult(null); } }}
            >
              <ToggleButton value="sandbox" sx={{ textTransform: 'none', px: 2 }}>Sandbox (test)</ToggleButton>
              <ToggleButton value="live" sx={{ textTransform: 'none', px: 2 }}>Live (real payments)</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {form.environment === 'live'
                ? 'Customers will be charged real money using the Live credentials.'
                : form.environment === 'sandbox'
                  ? 'Payments use test keys — no real money moves.'
                  : 'Choose explicitly — nothing is preselected so Live is never turned on by accident.'}
            </Typography>
            {form.environment === 'live' && row?.environment === 'sandbox' && (
              <Alert severity="warning" sx={{ mt: 1 }}>Switching to Live: real customer payments will use the Live credentials once saved.</Alert>
            )}
          </Box>
        )}

        <TextField
          required
          fullWidth
          label="Priority"
          value={form.priority}
          onChange={(e) => setForm({ ...form, priority: e.target.value.replace(/[^\d]/g, '').slice(0, 2) })}
          inputProps={{ inputMode: 'numeric' }}
          helperText={`Whole number ${view?.priorityRange.min ?? 1}–${view?.priorityRange.max ?? 99}; lower is tried first. Must be unique among enabled gateways and is ignored while disabled.${takenText ? ` In use: ${takenText}.` : ''}`}
        />

        {def && isOnline && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <Tabs value={tab} onChange={(_, v: Env) => { setTab(v); setTestResult(null); }} sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}>
              {(['sandbox', 'live'] as Env[]).map((e) => {
                const hasError = Object.keys(errors.fields).some((k) => k.startsWith(`${e}.`));
                return (
                  <Tab
                    key={e}
                    value={e}
                    sx={{ textTransform: 'none', fontWeight: 650 }}
                    label={
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <span>{ENV_LABEL[e]} credentials</span>
                        {form.environment === e && <Chip size="small" label="In use" color="primary" sx={{ height: 18, fontSize: '0.62rem' }} />}
                        {hasError && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'error.main' }} />}
                      </Stack>
                    }
                  />
                );
              })}
            </Tabs>
            <Stack spacing={1.75} sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {ENV_LABEL[tab]} and {ENV_LABEL[tab === 'sandbox' ? 'live' : 'sandbox']} keys are stored separately.
                {form.environment === tab ? ' Required fields must be complete for the environment in use.' : ' Optional until you switch to this environment.'}
                {' '}Saved secrets are never shown — leave a field blank to keep the saved value.
              </Typography>
              {tabFields.map((field) => {
                const key = `${tab}.${field.key}`;
                const saved = tabSaved?.fields.find((x) => x.key === field.key);
                const err = errors.fields[key];
                return (
                  <TextField
                    key={key}
                    fullWidth
                    required={field.required && form.environment === tab}
                    label={field.label}
                    type={field.secret && !reveal[key] ? 'password' : 'text'}
                    autoComplete="off"
                    value={form.credentials[tab][field.key] || ''}
                    onChange={(e) => {
                      setForm({ ...form, credentials: { ...form.credentials, [tab]: { ...form.credentials[tab], [field.key]: e.target.value } } });
                      setTestResult(null);
                    }}
                    placeholder={saved?.set ? 'Leave blank to keep saved value' : field.formatHint[tab] ? `e.g. ${field.formatHint[tab]}` : undefined}
                    error={!!err}
                    helperText={err || (saved?.set ? `Saved: ${saved.preview}` : field.formatHint[tab] ? `Format: ${field.formatHint[tab]}` : field.required ? undefined : 'Optional')}
                    InputProps={field.secret ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setReveal((r) => ({ ...r, [key]: !r[key] }))} edge="end">
                            {reveal[key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    } : undefined}
                  />
                );
              })}
              {def.testable ? (
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<NetworkCheck />}
                    disabled={testMut.isPending || (mode === 'create' && !form.type)}
                    onClick={() => testMut.mutate(tab)}
                    sx={{ textTransform: 'none', fontWeight: 650 }}
                  >
                    {testMut.isPending ? 'Testing…' : `Test ${ENV_LABEL[tab]} connection`}
                  </Button>
                  {!testResult && tabSaved?.verification && tabSaved.verification.status !== 'unverified' && (
                    <Typography variant="caption" color={tabSaved.verification.status === 'verified' ? 'success.main' : 'error.main'}>
                      Last test: {tabSaved.verification.message}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">Connection test is not available for {def.label}.</Typography>
              )}
              {testResult && testResult.environment === tab && (
                <Alert severity={testResult.ok ? 'success' : 'error'}>{testResult.message}</Alert>
              )}
            </Stack>
          </Box>
        )}

        {def && !isOnline && (
          <Alert severity="info">{def.label} is handled inside the app and needs no credentials.</Alert>
        )}

        <Box>
          <FormControlLabel
            control={<Switch checked={form.enabled} disabled={!canEnable} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
            label="Enabled"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
            {!def
              ? 'Select a gateway type first.'
              : !def.checkout
                ? `${def.label} cannot be enabled until it is integrated with checkout.`
                : !canEnable
                  ? `Complete the ${env ? ENV_LABEL[env as Env] : ''} credentials before enabling.`
                  : def.testable
                    ? 'Saving with Enabled on runs a connection test first — the gateway is only enabled if the provider accepts the keys.'
                    : 'Enabled gateways are available for new payments right away.'}
          </Typography>
        </Box>

        {errors.list.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={650}>Fix these before saving:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              {errors.list.map((e) => <li key={e}><Typography variant="body2">{e}</Typography></li>)}
            </Box>
          </Alert>
        )}
      </Stack>
    </FormDrawer>
  );
}

export default function PaymentGatewaysPage() {
  const queryClient = useQueryClient();
  const permissions = useSelector((s: { auth: { permissions: string[] } }) => s.auth.permissions);
  const userType = useSelector((s: { auth: { user: { userType?: string } | null } }) => s.auth.user?.userType);
  const canManage = userType === 'super_admin'
    || hasPermissionMatch(permissions, 'settings.settings.update')
    || hasPermissionMatch(permissions, 'settings.settings.edit');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formRow, setFormRow] = useState<GatewayRow | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'disable' | 'enable' | 'delete'; row: GatewayRow } | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const { data: view, isLoading } = useQuery({
    queryKey: ['payment-gateways-admin'],
    queryFn: async () => (await api.get('/payments/gateways/admin')).data.data as AdminView,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['payment-gateways-admin'] });
    queryClient.invalidateQueries({ queryKey: ['master-data'] });
  };

  const toggleMut = useMutation({
    mutationFn: async ({ row, enabled }: { row: GatewayRow; enabled: boolean }) =>
      api.put(`/payments/gateways/${row.type}`, { enabled }),
    onSuccess: (_, { row, enabled }) => {
      refresh();
      setToast({ message: `${row.label} ${enabled ? 'enabled' : 'disabled'}`, severity: 'success' });
    },
    onError: (err) => setToast({ message: apiMessage(err, 'Could not update gateway'), severity: 'error' }),
  });

  const deleteMut = useMutation({
    mutationFn: async (row: GatewayRow) => api.delete(`/payments/gateways/${row.type}`),
    onSuccess: (_, row) => { refresh(); setToast({ message: `${row.label} removed`, severity: 'success' }); },
    onError: (err) => setToast({ message: apiMessage(err, 'Could not remove gateway'), severity: 'error' }),
  });

  const testMut = useMutation({
    mutationFn: async (row: GatewayRow) => (await api.post(`/payments/gateways/${row.type}/test`, {})).data.data as { ok: boolean; message: string },
    onSuccess: (result) => { refresh(); setToast({ message: result.message, severity: result.ok ? 'success' : 'error' }); },
    onError: (err) => setToast({ message: apiMessage(err, 'Test failed'), severity: 'error' }),
  });

  const openForm = (mode: 'create' | 'edit', row: GatewayRow | null) => {
    setFormMode(mode);
    setFormRow(row);
    setFormOpen(true);
  };

  const rows = view?.gateways || [];
  const canAddMore = (view?.catalog || []).some((c) => !c.added);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={700}>Payment Gateways</Typography>
        {canManage && (
          <Button variant="contained" startIcon={<Add />} onClick={() => openForm('create', null)} disabled={!canAddMore}>
            Add Gateway
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Gateways process customer payments. Lower priority numbers are tried first; priority is ignored while a gateway is disabled.
        Disabling a gateway immediately switches off the payment methods that use it.
      </Typography>

      {view && (
        <Alert severity={view.routing.order.length ? 'info' : 'error'} sx={{ mb: 2 }}>
          <strong>Current routing:</strong> {view.routing.message}
        </Alert>
      )}

      {isLoading && <Typography>Loading...</Typography>}

      <Stack spacing={1}>
        {rows.map((row) => {
          const roleLabel = row.routing.role === 'default' ? 'Default' : row.routing.role === 'fallback' ? 'Fallback' : row.routing.role === 'backup' ? `Backup #${row.routing.order}` : null;
          return (
            <Box key={row.type} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={700}>{row.label}</Typography>
                    <Chip size="small" variant="outlined" label={row.kind === 'online' ? 'Online' : 'Offline'} sx={{ height: 20, fontSize: '0.65rem' }} />
                    {roleLabel && <Chip size="small" color="primary" label={roleLabel} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />}
                  </Stack>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={row.enabled ? 'Enabled' : 'Disabled'} color={row.enabled ? 'success' : 'default'} sx={{ fontWeight: 650 }} />
                    {row.environment && (
                      <Chip size="small" variant="outlined" color={row.environment === 'live' ? 'error' : 'warning'} label={`Environment: ${ENV_LABEL[row.environment]}`} />
                    )}
                    <Tooltip title={row.readiness.detail}>
                      <Chip size="small" variant="outlined" color={READINESS_COLOR[row.readiness.state] || 'default'} label={row.readiness.label} />
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {row.setUp && row.priority != null
                      ? (row.enabled ? `Priority ${row.priority}` : `Priority ${row.priority} (ignored while disabled)`)
                      : 'Priority —'}
                    {' · '}
                    {row.activeMethods.length
                      ? `Used by ${row.activeMethods.map((m) => m.name).join(', ')}`
                      : 'No active payment methods'}
                    {row.transactions > 0 && ` · ${row.transactions} transaction${row.transactions === 1 ? '' : 's'}`}
                  </Typography>
                </Box>

                {canManage && (
                  <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                    {row.setUp ? (
                      <>
                        {row.kind === 'online' && (
                          <Button size="small" variant="outlined" onClick={() => openForm('edit', row)}>Configure</Button>
                        )}
                        {row.kind === 'offline' && (
                          <Button size="small" variant="outlined" onClick={() => openForm('edit', row)}>Priority</Button>
                        )}
                        {row.testable && row.credentials && row.environment && row.credentials[row.environment].complete && (
                          <Tooltip title={`Test the saved ${ENV_LABEL[row.environment]} credentials`}>
                            <span>
                              <IconButton size="small" onClick={() => testMut.mutate(row)} disabled={testMut.isPending}>
                                <NetworkCheck fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title={row.toggleLock || (row.enabled ? 'Disable' : 'Enable')}>
                          <span>
                            <Switch
                              checked={row.enabled}
                              disabled={!!row.toggleLock || toggleMut.isPending}
                              onChange={(e) => setConfirm({ kind: e.target.checked ? 'enable' : 'disable', row })}
                            />
                          </span>
                        </Tooltip>
                        <Tooltip title={row.deleteLock || 'Remove gateway'}>
                          <span>
                            <IconButton size="small" color="error" disabled={!!row.deleteLock} onClick={() => setConfirm({ kind: 'delete', row })}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <Button size="small" variant="contained" onClick={() => {
                        const def = view?.catalog.find((c) => c.type === row.type);
                        setFormMode('create');
                        setFormRow({ ...row, setUp: false, credentials: def?.kind === 'online' ? null : row.credentials });
                        setFormOpen(true);
                      }}>
                        Set up
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <GatewayFormDrawer
        open={formOpen}
        mode={formMode}
        row={formRow}
        view={view}
        onClose={() => setFormOpen(false)}
        onSaved={(message) => setToast({ message, severity: 'success' })}
      />

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (
          <>
            <DialogTitle>
              {confirm.kind === 'delete' ? `Remove ${confirm.row.label}?` : `${confirm.kind === 'enable' ? 'Enable' : 'Disable'} ${confirm.row.label}?`}
            </DialogTitle>
            <DialogContent>
              {confirm.kind === 'disable' && (
                <Stack spacing={1}>
                  <Typography variant="body2">{confirm.row.label} will stop accepting new payments immediately.</Typography>
                  {confirm.row.activeMethods.length > 0 ? (
                    <Alert severity="warning">
                      These payment methods will be switched off: <strong>{confirm.row.activeMethods.map((m) => m.name).join(', ')}</strong>.
                      Re-enabling the gateway does not switch them back on — reactivate them in Payment Methods.
                    </Alert>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No active payment methods use it.</Typography>
                  )}
                  {confirm.row.routing.role === 'default' && (
                    <Typography variant="body2" color="text.secondary">It is the current default; the next gateway by priority takes over.</Typography>
                  )}
                </Stack>
              )}
              {confirm.kind === 'enable' && (
                <Typography variant="body2">
                  {confirm.row.testable
                    ? `A connection test runs with the ${confirm.row.environment ? ENV_LABEL[confirm.row.environment] : ''} credentials; ${confirm.row.label} is enabled only if it passes.`
                    : `${confirm.row.label} becomes available for new payments.`}
                  {confirm.row.environment === 'live' && ' Customers will be charged real money.'}
                </Typography>
              )}
              {confirm.kind === 'delete' && (
                <Typography variant="body2">
                  Its saved configuration and credentials are deleted. Gateways with transactions cannot be removed — keep them disabled instead.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={confirm.kind === 'enable' ? 'primary' : 'error'}
                onClick={() => {
                  if (confirm.kind === 'delete') deleteMut.mutate(confirm.row);
                  else toggleMut.mutate({ row: confirm.row, enabled: confirm.kind === 'enable' });
                  setConfirm(null);
                }}
              >
                {confirm.kind === 'delete' ? 'Remove' : confirm.kind === 'enable' ? 'Enable' : 'Disable'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
